const convertToISODate = (dateString) => {
    if (!dateString) return null;
    const [month, day, year] = dateString.split('-');
    return `${year}-${month}-${day}`;
};

const extractCPTAndUnit = (value) => {
    if (!value) {
        return { cpt: '', unit: 1 };
    }
    if (typeof value === 'string' && value.includes('*')) {
        const [cpt, unit] = value.split('*');
        return { cpt: cpt.trim(), unit: parseInt(unit, 10) || 1 };
    }
    return { cpt: value.trim(), unit: 1 };
};

const extractDynamicValues = (data, baseKey) => {
    if (!data || Object.values(data).every((value) => value === null || value === undefined)) {
        return { cpts: [], units: [] };
    }

    const cpts = [];
    const units = [];

    if (data?.[baseKey] !== undefined) {
        const { cpt, unit } = extractCPTAndUnit(data[baseKey]);
        cpts.push(cpt || '');
        units.push(unit);
    }

    for (let i = 1; ; i++) {
        const key = `${baseKey}_${i}`;
        if (!(key in data)) {
            if (i === 1) continue;
            else break;
        }
        if (data[key]) {
            const { cpt, unit } = extractCPTAndUnit(data[`${baseKey}_${i}`]);
            cpts.push(cpt || '');
            units.push(unit);
        }
    }

    while (cpts.length > 0 && cpts[cpts.length - 1] === '') {
        cpts.pop();
        units.pop();
    }

    return { cpts: cpts || [], units: units || [] };
};

const extractDynamicModifierValues = (data, baseKey, cptKey) => {
    if (!data || Object.values(data).every((value) => value === null || value === undefined)) {
        return [];
    }

    const cpts = [];
    if (data?.[cptKey] !== undefined) {
        cpts.push(data[cptKey] || '');
    }

    let cptIndex = 2;
    while (data?.[`${cptKey}_${cptIndex}`] !== undefined) {
        cpts.push(data[`${cptKey}_${cptIndex}`] || '');
        cptIndex++;
    }

    // 2. Extract modifier strings using baseKey
    const modifiersRaw = [];
    if (data?.[baseKey] !== undefined) {
        modifiersRaw.push(data[baseKey] || '');
    }

    let modIndex = 2;
    while (data?.[`${baseKey}_${modIndex}`] !== undefined) {
        modifiersRaw.push(data[`${baseKey}_${modIndex}`] || '');
        modIndex++;
    }

    // 3. Trim trailing blanks (optional, to clean up data)
    while (modifiersRaw.length > 0 && modifiersRaw[modifiersRaw.length - 1] === '') {
        modifiersRaw.pop();
    }

    // 4. Build modifier objects based on CPTs
    const result = cpts.map((_, index) => {
        const value = modifiersRaw[index] || '';

        if (typeof value === 'string' && value.includes(',')) {
            const parts = value.split(',').map((part) => part.trim());
            const [primary, secondary1, ...rest] = parts;
            const secondary2 = rest.length ? rest.join(',') : '';

            return {
                primary: primary || '',
                secondary1: secondary1 || '',
                secondary2: secondary2 || '',
            };
        }

        return {
            primary: value || '',
            secondary1: '',
            secondary2: '',
        };
    });

    return result;

};

const extractDynamicICDValues = (data, baseKey, cptKeyBase) => {
    if (!data || Object.values(data).every(value => value == null)) return [];

    const icdKeyMap = Object.entries(data)
        .filter(([key]) => key.startsWith(baseKey))
        .reduce((acc, [key, val]) => {
            if (val) acc[key] = val;
            return acc;
        }, {});

    const cptKeyMap = Object.entries(data)
        .filter(([key]) => key.startsWith(cptKeyBase))
        .reduce((acc, [key, val]) => {
            if (val) acc[key] = val;
            return acc;
        }, {});
                
    const connectionMap =
        baseKey === 'billed_icd' ? data?.billedCptIcdMap || {} :
            baseKey === 'audited_icd' ? data?.auditedCptIcdMap || {} :
                {};

    const result = Object.keys(cptKeyMap).map(cptKey => {
        const icdKeys = connectionMap[cptKey] || [];
        return icdKeys.map(icdKey => icdKeyMap[icdKey]).filter(Boolean);
    });

    const allICDValues = Object.values(icdKeyMap);
    const icdValueCount = {};
    allICDValues.forEach(icd => {
        icdValueCount[icd] = (icdValueCount[icd] || 0) + 1;
    });

    const usedICDCount = {};
    result.flat().forEach(icd => {
        usedICDCount[icd] = (usedICDCount[icd] || 0) + 1;
    });

    const unmappedICDs = [];
    Object.keys(icdValueCount).forEach(icd => {
        const remaining = icdValueCount[icd] - (usedICDCount[icd] || 0);
        for (let i = 0; i < remaining; i++) {
            unmappedICDs.push(icd);
        }
    });

    if (Array.isArray(result[result.length - 1]) && unmappedICDs.length) {
        result[result.length - 1].push(...unmappedICDs);
    }

    return result;
};

const formatDynamicCPTValues = (cptArray, unitArray, baseKey) => {
    const dynamicAttributes = {};

    let dynamicIndex = 2; // Start from _2 for additional entries

    cptArray.forEach((cpt, index) => {
        let formattedValue = cpt || null;

        const unit = unitArray && unitArray[index] ? unitArray[index] : 1;
        if (cpt && unit > 1) {
            formattedValue = `${cpt}*${unit}`;
        }

        if (index === 0) {
            dynamicAttributes[baseKey] = formattedValue;
        } else {
            dynamicAttributes[`${baseKey}_${dynamicIndex}`] = formattedValue;
            dynamicIndex++;
        }
    });

    return dynamicAttributes;
};

const formatDynamicICDValues = (icdMainArray, baseKey) => {
    const dynamicAttributes = {};
    const seenICDs = new Set();
    let dynamicIndex = 1;

    icdMainArray.flat().forEach((icd) => {
        if (!seenICDs.has(icd)) {
            const key = dynamicIndex === 1 ? baseKey : `${baseKey}_${dynamicIndex}`;
            dynamicAttributes[key] = icd;
            seenICDs.add(icd);
            dynamicIndex++;
        }
    });

    return dynamicAttributes;
};


const formatDynamicModifierValues = (array, baseKey) => {
    const dynamicAttributes = {};

    let dynamicIndex = 2; // Start from _2 for additional entries

    array.forEach((value, index) => {
        let formattedValue = null;

        if (typeof value === "object" && value !== null) {
            const { primary, secondary1, secondary2 } = value;
            const parts = [primary, secondary1, secondary2].filter((part) => part?.trim() !== "");
            formattedValue = parts.length > 0 ? parts.join(",") : null;
        } else {
            formattedValue = value || null;
        }

        if (index === 0) {
            dynamicAttributes[baseKey] = formattedValue;
        } else {
            dynamicAttributes[`${baseKey}_${dynamicIndex}`] = formattedValue;
            dynamicIndex++;
        }
    });

    return dynamicAttributes;
};

const resetDynamicKeys = (data, baseKey) => {
    if (!data) {
        return {};
    }
    const keysToReset = Object.keys(data).filter((key) => key.startsWith(baseKey));
    return keysToReset.reduce((acc, key) => {
        acc[key] = null;
        return acc;
    }, {});
};

const buildCptIcdMap = (cptCodes, icdCodeLists, cptKey, icdKey) => {
    const mapping = {};
    let cptIndex = 1;
    let icdIndex = 1;

    const icdToKeyMap = new Map();
    const keyAssignment = [];

    // Step 1: Assign unique keys to unique ICDs
    icdCodeLists.flat().forEach((icd) => {
        if (!icdToKeyMap.has(icd)) {
            const ref = icdIndex === 1 ? icdKey : `${icdKey}_${icdIndex}`;
            icdToKeyMap.set(icd, ref);
            icdIndex++;
        }
        keyAssignment.push(icdToKeyMap.get(icd));
    });

    // Step 2: Build mapping for CPTs using assigned keys
    let assignmentIndex = 0;
    cptCodes.forEach(() => {
        const cptRef = cptIndex === 1 ? cptKey : `${cptKey}_${cptIndex}`;
        const icdCount = icdCodeLists[cptIndex - 1]?.length || 0;

        mapping[cptRef] = keyAssignment.slice(assignmentIndex, assignmentIndex + icdCount);
        assignmentIndex += icdCount;
        cptIndex++;
    });

    return mapping;
};


export {
    convertToISODate,
    extractDynamicValues,
    extractDynamicModifierValues,
    extractDynamicICDValues,
    formatDynamicCPTValues,
    formatDynamicModifierValues,
    formatDynamicICDValues,
    resetDynamicKeys,
    buildCptIcdMap
};


// Previous implementation where icd are duplicated as entered
// const formatDynamicICDValues = (icdMainArray, baseKey) => {
//     const dynamicAttributes = {};
//     let dynamicIndex = 2;
//     let isFirst = true;

//     icdMainArray.forEach((icdArray) => {
//         icdArray.forEach((icd) => {
//             const formattedValue = icd || null;

//             if (isFirst) {
//                 dynamicAttributes[baseKey] = formattedValue;
//                 isFirst = false;
//             } else {
//                 dynamicAttributes[`${baseKey}_${dynamicIndex}`] = formattedValue;
//                 dynamicIndex++;
//             }
//         });
//     });

//     return dynamicAttributes;

// };

// const buildCptIcdMap1 = (cptCodes, icdCodeLists, cptKey, icdKey) => {
//     const mapping = {};
//     let cptIndex = 1;
//     let icdIndex = 1;

//     cptCodes.forEach((cpt, index) => {
//         const cptRef = cptIndex === 1 ? cptKey : `${cptKey}_${cptIndex}`;
//         const icdRefs = icdCodeLists[index].map(() => {
//             const ref = icdIndex === 1 ? icdKey : `${icdKey}_${icdIndex}`;
//             icdIndex++;
//             return ref;
//         });

//         mapping[cptRef] = icdRefs;
//         cptIndex++;
//     });

//     console.log("mapping", mapping);

//     return mapping;
// };

// const buildCptIcdMap = (cptCodes, icdCodeLists) => {
//     const mapping = {};

//     cptCodes.forEach((cpt, index) => {
//         const icds = icdCodeLists[index] || [];
//         if (!mapping[cpt]) {
//             mapping[cpt] = [...icds];
//         } else {
//             mapping[cpt] = [...mapping[cpt], ...icds];
//         }
//     });

//     return mapping;
// };


//***********************************************************************


// const extractValuesNew = (data, baseKey) => {
//     const icds = [];

//     if (data?.[baseKey]) {
//         icds.push(data[baseKey]);
//     }

//     for (let i = 1; ; i++) {
//         const key = `${baseKey}_${i}`;
//         if (!(key in data)) {
//             if (i === 1) continue;
//             else break;
//         }
//         if (data[key]) {
//             icds.push(data[key]);
//         }
//     }

//     return icds;
// };

// const getKeyToValueMap = (data, baseKey) => {
//     const keyMap = {};
//     Object.keys(data).forEach(key => {
//         if (key === baseKey || key.startsWith(baseKey + "_")) {
//             keyMap[key] = data[key];
//         }
//     });
//     return keyMap;
// };


// const extractDynamicICDValues3 = (data, baseKey, cptKeyBase) => {
//     if (!data || Object.values(data).every(value => value === null || value === undefined)) {
//         return [];
//     }

//     const icds = extractValuesNew(data, baseKey);
//     const cpts = extractValuesNew(data, cptKeyBase);

//     console.log("icds", icds, "cpts", cpts);

//     const icdKeyMap = getKeyToValueMap(data, baseKey);       // e.g., { icdKey: "C00.0", icdKey_2: "D00.0", ... }
//     const cptKeyMap = getKeyToValueMap(data, cptKeyBase);     // e.g., { cptKey: "99203", cptKey_2: "99204", ... }

//     console.log("icdKeyMap", icdKeyMap, "cptKeyMap", cptKeyMap);

//     const mapKey =
//         baseKey === 'billed_icd' ? 'billedCptIcdMap' :
//         baseKey === 'audited_icd' ? 'auditedCptIcdMap' : null;

//     const connectionMap = mapKey && data?.[mapKey] ? data[mapKey] : {};

//     // Reverse key lookup from actual CPT value to cptKey
//     const cptValueToKey = Object.entries(cptKeyMap).reduce((acc, [key, val]) => {
//         acc[val] = key;
//         return acc;
//     }, {});
//     console.log("cptValueToKey", cptValueToKey);

//     const result = cpts.map(() => []);

//     // Count ICD occurrences
//     const icdCount = {};
//     icds.forEach(icd => {
//         icdCount[icd] = (icdCount[icd] || 0) + 1;
//     });

//     const usedICDCount = {};

//     cpts.forEach((cpt, index) => {
//         const cptRefKey = cptValueToKey[cpt];
//         const mappedIcdRefs = connectionMap[cptRefKey] || [];

//         mappedIcdRefs.forEach(icdKey => {
//             const icdValue = icdKeyMap[icdKey];
//             if (icds.includes(icdValue)) {
//                 result[index].push(icdValue);
//                 usedICDCount[icdValue] = (usedICDCount[icdValue] || 0) + 1;
//             }
//         });
//     });

//     // Find unmapped ICDs
//     const unmappedICDs = [];

//     Object.keys(icdCount).forEach(icd => {
//         const total = icdCount[icd];
//         const used = usedICDCount[icd] || 0;
//         const remaining = total - used;

//         for (let i = 0; i < remaining; i++) {
//             unmappedICDs.push(icd);
//         }
//     });

//     if (result.length > 0 && unmappedICDs.length > 0) {
//         result[result.length - 1].push(...unmappedICDs);
//     }

//     return result;
// };

